import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { KundeService, PartnerService } from 'src/app/api/services';
import { PartnerDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-dto';
import { PartnerGatewayEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/partner-gateway-enum';
import { PartnerCalculationModelEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/partner-calculation-model-enum';
import { UpdatePartnerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/update-partner-request';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { CreatePartnerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/create-partner-request';
import { HttpErrorResponse } from '@angular/common/http';
import { PartnerExternalInfoDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-external-info-dto';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-partner-edit-dialog',
  templateUrl: './partner-edit-dialog.component.html',
  styleUrls: ['./partner-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PartnerEditDialogComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  gateways: PartnerGatewayEnum[] = [];
  calculationModels: PartnerCalculationModelEnum[] = [];
  minValidToDate: Date;
  partnerInformation: PartnerExternalInfoDto;
  fetchingPartnerInformation: Boolean;
  partnerCalculationModelEnum = PartnerCalculationModelEnum;

  unsubscribe$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private partnerService: PartnerService,
    private kundeService: KundeService,
    public dialog: MatDialog,
    private errorHandler: ErrorHandlerService,
    private loader: LoaderService,
    public dialogRef: MatDialogRef<PartnerEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public partner: PartnerDto,
  ) {}

  ngOnInit(): void {
    this.gateways = this.enumToArray<PartnerGatewayEnum>(PartnerGatewayEnum);
    this.calculationModels = this.enumToArray<PartnerCalculationModelEnum>(PartnerCalculationModelEnum);
    this.minValidToDate = this.partner.validFrom ? new Date(this.partner.validFrom) : null;

    this.form = this.formBuilder.group({
      name: [this.partner.name, [Validators.required, Validators.minLength(3)]],
      partnerNo: [this.partner.partnerNo],
      email: [this.partner.email],
      phone: [this.partner.phone],
      partnerGateway: [this.partner.partnerGateway, [Validators.required]],
      partnerCalculationModel: [this.partner.partnerCalculationModel, [Validators.required]],
      percentageCutServiceaftale: [this.toPercent(this.partner.percentageCutServiceaftale), [Validators.required]],
      percentageCutServiceordreHours: [this.toPercent(this.partner.percentageCutServiceordreHours), [Validators.required]],
      percentageCutServiceordreMaterials: [this.toPercent(this.partner.percentageCutServiceordreMaterials), [Validators.required]],
      validFrom: [this.partner.validFrom, [Validators.required]],
      validTo: [this.partner.validTo],
      okKundeAftalenrGas: [this.partner.okKundeAftalenrGas],
      okKundeAftalenrOlie: [this.partner.okKundeAftalenrOlie],
      okKundeAftalenrVarmepumpe: [this.partner.okKundeAftalenrVarmepumpe],
      receiveDailyReport: [this.partner.receiveDailyReport],
      receiveMonthlyReport: [this.partner.receiveMonthlyReport],
      isOrderEditingAllowed: [this.partner.isOrderEditingAllowed],
      autoClosePhoneServiceOrders: [this.partner.autoClosePhoneServiceOrders],
      createInspectionServiceOrdersAutomatically: [this.partner.createInspectionServiceOrdersAutomatically],
      approveServiceOrdersAutomatically: [this.partner.approveServiceOrdersAutomatically],
    });

    this.form
      .get('validFrom')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((val) => {
        this.minValidToDate = val;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  get isCreateDialog() {
    return this.partner.id == null;
  }

  async save() {
    if (!this.form.valid) return;

    let data: PartnerDto = Object.assign({}, this.partner, this.form.value);

    if (this.partner.id) {
      this.updatePartner(data);
    } else {
      this.createPartner(data);
    }
  }

  async updatePartner(partner: PartnerDto) {
    let request: UpdatePartnerRequest = {
      name: partner.name,
      email: partner.email,
      phoneString: partner.phone,
      partnerGateway: partner.partnerGateway,
      partnerCalculationModel: partner.partnerCalculationModel,
      percentageCutServiceaftale: partner.percentageCutServiceaftale ? this.toDecimal(partner.percentageCutServiceaftale) : 1,
      percentageCutServiceordreHours: partner.percentageCutServiceordreHours ? this.toDecimal(partner.percentageCutServiceordreHours) : 1,
      percentageCutServiceordreMaterials: this.toDecimal(partner.percentageCutServiceordreMaterials),
      validFrom: new Date(partner.validFrom).toOkDateString(),
      validTo: partner.validTo ? new Date(partner.validTo).toOkDateString() : null,
      okKundeAftalenrGas: partner.okKundeAftalenrGas,
      okKundeAftalenrOlie: partner.okKundeAftalenrOlie,
      okKundeAftalenrVarmepumpe: partner.okKundeAftalenrVarmepumpe,
      receiveDailyReport: partner.receiveDailyReport,
      receiveMonthlyReport: partner.receiveMonthlyReport,
      isOrderEditingAllowed: partner.isOrderEditingAllowed,
      autoClosePhoneServiceOrders: partner.autoClosePhoneServiceOrders,
      createInspectionServiceOrdersAutomatically: partner.createInspectionServiceOrdersAutomatically,
      approveServiceOrdersAutomatically: partner.approveServiceOrdersAutomatically,
    };

    this.loader.show();
    await this.partnerService
      .updatePartner({
        id: this.partner.id,
        caller: EGIS_SERVICE_CALLER,
        body: request,
      })
      .toPromise()
      .then((response) => {
        this.dialogRef.close(response);
      })
      .catch((error) => {
        this.errorHandler.HandleError(error);
      })
      .finally(() => {
        this.loader.hide();
      });
  }

  async createPartner(partner: PartnerDto) {
    let request: CreatePartnerRequest = {
      name: partner.name,
      email: partner.email,
      phoneString: partner.phone,
      partnerGateway: partner.partnerGateway,
      partnerCalculationModel: partner.partnerCalculationModel,
      percentageCutServiceaftale: partner.percentageCutServiceaftale ? this.toDecimal(partner.percentageCutServiceaftale) : 1,
      percentageCutServiceordreHours: partner.percentageCutServiceordreHours ? this.toDecimal(partner.percentageCutServiceordreHours) : 1,
      percentageCutServiceordreMaterials: this.toDecimal(partner.percentageCutServiceordreMaterials),
      validFrom: new Date(partner.validFrom).toOkDateString(),
      validTo: partner.validTo ? new Date(partner.validTo).toOkDateString() : null,
      okKundeAftalenrGas: partner.okKundeAftalenrGas,
      okKundeAftalenrOlie: partner.okKundeAftalenrOlie,
      okKundeAftalenrVarmepumpe: partner.okKundeAftalenrVarmepumpe,
      receiveDailyReport: partner.receiveDailyReport ?? false,
      receiveMonthlyReport: partner.receiveMonthlyReport ?? false,
      isOrderEditingAllowed: partner.isOrderEditingAllowed ?? false,
      autoClosePhoneServiceOrders: partner.autoClosePhoneServiceOrders ?? false,
      createInspectionServiceOrdersAutomatically: partner.createInspectionServiceOrdersAutomatically ?? false,
      approveServiceOrdersAutomatically: partner.approveServiceOrdersAutomatically ?? false,
    };

    this.loader.show();
    await this.partnerService
      .createPartner({
        caller: EGIS_SERVICE_CALLER,
        body: request,
      })
      .toPromise()
      .then((response) => {
        this.dialogRef.close(response);
      })
      .catch((error) => {
        this.errorHandler.HandleError(error);
      })
      .finally(() => {
        this.loader.hide();
      });
  }

  enumToArray<T>(enumValue: any): T[] {
    return Object.values(enumValue).filter((value) => typeof value !== 'number') as T[];
  }

  toPercent(floatingNumber: number): number {
    return floatingNumber * 100;
  }

  toDecimal(integerNumber: number): number {
    return integerNumber / 100;
  }

  openPartnerInfo(okKundeAftalenr: number) {
    this.fetchingPartnerInformation = true;

    this.kundeService.getPartnerInformation({ okKundeAftalenr: okKundeAftalenr, caller: EGIS_SERVICE_CALLER }).subscribe(
      (partnerInfo) => {
        this.fetchingPartnerInformation = false;
        this.partnerInformation = partnerInfo;
      },
      (error: HttpErrorResponse) => {
        this.fetchingPartnerInformation = false;
        this.errorHandler.HandleError(error);
      },
    );
  }

  hidePartnerInformation() {
    this.partnerInformation = null;
  }
}
